import { useCallback, useEffect, useRef, useState } from 'react';
import { NESTED_FIELD_KEY, setAnswerAtPath, stripClientOnlyFields } from './answerPath';
import {
  readLocalDraft,
  writeLocalDraft,
  clearLocalDraft,
  LOCAL_DRAFT_AUTO_SAVE_DELAY_MS as LOCAL_DEBOUNCE_MS,
} from './localDraft';

const SERVER_AUTO_SAVE_DELAY_MS = 1500;
const RESTORED_STATUS_CLEAR_MS = 3000;

/**
 * Shared answer-state core for every respondent surface (standalone trigger form, agent
 * form, form-flow step). Consumes a {@link import('./adapters/coreFormAdapterTypes').CoreFormAdapter}
 * — the wrapping hook (`usePublicForm`, `useFormFlow`) picks the adapter and layers
 * transport-specific concerns (bootstrap, submit outcomes, SSE, stepper, gate state) on
 * top.
 *
 * Everything in here is surface-agnostic: local + server autosave, "Saved X ago" state,
 * file upload/remove/retry with serialization and pending tiles, localStorage backup,
 * `_file` stripping. New behaviour that applies to every respondent surface belongs
 * in this file so it can't drift across entry points.
 *
 * ## Caller contract (not enforced, but mandatory for correctness)
 *
 *   1. **`adapter` MUST be memoized** across renders (via `useMemo` / factory closed
 *      over stable deps). Several of the hook's `useCallback`s have `[adapter]` in
 *      their deps; a per-render adapter rebuilds every callback, churns
 *      `saveDraftRef`, and cancels/reschedules pending timers each keystroke. Every
 *      existing caller (`usePublicForm`, `useFormFlow`) memoizes correctly — copy that
 *      pattern for new surfaces.
 *
 *   2. **`resourceId` MUST stay stable** for the component's lifetime. It keys the
 *      localStorage draft and must match between the write on `onChangeAnswer` and the
 *      read on `restoreFromLocalDraft`. A mid-session change silently drops the user's
 *      in-flight answers on refresh. `PublicFormPage` specifically captures
 *      `instanceId` at mount via `useRef` to keep its resourceId stable across URL
 *      navigation — do the same for any new surface whose URL mutates mid-session.
 *
 * @param {Object} options
 * @param {import('./adapters/coreFormAdapterTypes').CoreFormAdapter} options.adapter
 * @param {string} options.resourceId  Stable identifier used to key the localStorage
 *     draft backup (componentId, formSessionId, agent-form token, etc.).
 * @param {string|null} [options.grantToken]  Magic-link grant token; passed through to
 *     every adapter method.
 * @param {boolean} [options.submitting]  Wrapper-owned flag. While true, the debounced
 *     server autosave suspends — the submit path is about to persist a richer payload
 *     and we don't want the two writes to race.
 */
export default function useFormAnswers({ adapter, resourceId, grantToken = null, submitting = false }) {

  const [answers, setAnswers] = useState({});
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'restored'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const answersRef = useRef({});
  useEffect(() => { answersRef.current = answers; }, [answers]);

  const autoSaveTimerRef = useRef(null);
  const serverAutoSaveTimerRef = useRef(null);
  const saveDraftRef = useRef(null);
  const restoredClearTimerRef = useRef(null);

  // Explicit "Save draft" trigger. Uses `savingDraft` (distinct from the wrapper's
  // `submitting`) so clicking Save draft doesn't flip the Submit button to "Submitting".
  const saveDraft = useCallback(async () => {
    if (!adapter?.capabilities?.canSaveDraft) return null;
    if (serverAutoSaveTimerRef.current) clearTimeout(serverAutoSaveTimerRef.current);
    setSavingDraft(true);
    try {
      const data = await adapter.saveDraft(
        stripClientOnlyFields(answersRef.current),
        grantToken,
      );
      setLastSavedAt(Date.now());
      setAutoSaveStatus('saved');
      if (resourceId) clearLocalDraft(resourceId);
      return data || null;
    } catch (e) {
      setAutoSaveStatus(null);
      throw e;
    } finally {
      setSavingDraft(false);
    }
  }, [adapter, grantToken, resourceId]);

  useEffect(() => { saveDraftRef.current = saveDraft; }, [saveDraft]);

  // Wrapper-facing debounced server save. Called from onFieldBlur (standalone) or
  // onChangeAnswer (flow) whenever the wrapper decides the server should see the latest
  // answers. Does NOT flip savingDraft — the status label is driven by autoSaveStatus,
  // so the Save draft button stays stable.
  const requestServerAutoSave = useCallback(() => {
    if (!adapter?.capabilities?.canSaveDraft) return;
    if (submitting) return;
    if (serverAutoSaveTimerRef.current) clearTimeout(serverAutoSaveTimerRef.current);
    serverAutoSaveTimerRef.current = setTimeout(() => {
      const fn = saveDraftRef.current;
      if (typeof fn === 'function') {
        fn().catch(() => { /* best-effort; local backup already written */ });
      }
    }, SERVER_AUTO_SAVE_DELAY_MS);
  }, [adapter, submitting]);

  // Cancel any pending requestServerAutoSave firing. Wrappers must call this at the
  // start of submit/back paths so a keystroke-triggered autosave scheduled before the
  // transition doesn't fire against the NEW step's componentId with the OLD step's
  // answers. (The flowStepAdapter reads getCurrentComponentId() at call time, so a
  // late-firing timer would tag the payload against whatever step is current by then.)
  const cancelPendingServerAutoSave = useCallback(() => {
    if (serverAutoSaveTimerRef.current) {
      clearTimeout(serverAutoSaveTimerRef.current);
      serverAutoSaveTimerRef.current = null;
    }
  }, []);

  // Keystroke updater + debounced localStorage write. Standalone semantics (cheap,
  // device-local, always safe regardless of capabilities).
  const onChangeAnswer = useCallback((key, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      answersRef.current = next;
      return next;
    });
    if (!resourceId) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('saving');
    autoSaveTimerRef.current = setTimeout(() => {
      writeLocalDraft(resourceId, stripClientOnlyFields(answersRef.current));
      setAutoSaveStatus('saved');
      setLastSavedAt(Date.now());
    }, LOCAL_DEBOUNCE_MS);
  }, [resourceId]);

  // Serialized multi-file upload. Injects a pending FileTile (id=pending-*, uploading:true,
  // _file retained for Retry), awaits adapter.upload, replaces with the descriptor on
  // success or flips to error state on failure. Writes localStorage + schedules server
  // autosave after every successful upload so a refresh mid-session doesn't lose files.
  const onUpload = useCallback(async (fieldKey, files) => {
    if (!adapter?.capabilities?.canUpload) return;
    const list = Array.isArray(files) ? files : [files];
    for (const file of list) {
      const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const placeholder = {
        id: pendingId,
        pending: true,
        uploading: true,
        name: file.name,
        _file: file,
      };
      setAnswers((prev) => {
        const next = setAnswerAtPath(prev, fieldKey, (cur) => [...cur, placeholder]);
        answersRef.current = next;
        return next;
      });
      try {
        const descriptor = await adapter.upload(fieldKey, file, grantToken);
        setAnswers((prev) => {
          const next = setAnswerAtPath(prev, fieldKey, (cur) =>
            cur.map((f) => (f?.id === pendingId ? (descriptor || f) : f)),
          );
          answersRef.current = next;
          return next;
        });
        if (descriptor && resourceId) {
          writeLocalDraft(resourceId, stripClientOnlyFields(answersRef.current));
          requestServerAutoSave();
        }
      } catch {
        setAnswers((prev) => {
          const next = setAnswerAtPath(prev, fieldKey, (cur) =>
            cur.map((f) =>
              f?.id === pendingId
                ? { ...f, uploading: false, error: 'Upload failed' }
                : f,
            ),
          );
          answersRef.current = next;
          return next;
        });
      }
    }
  }, [adapter, grantToken, resourceId, requestServerAutoSave]);

  const retryUpload = useCallback(async (fieldKey, placeholderId) => {
    const m = NESTED_FIELD_KEY.exec(fieldKey);
    let entry = null;
    if (m) {
      const [, section, rowIdxStr, child] = m;
      const rows = answersRef.current?.[section];
      const row = Array.isArray(rows) ? rows[Number(rowIdxStr)] : null;
      const childList = row ? row[child] : null;
      entry = Array.isArray(childList) ? childList.find((f) => f?.id === placeholderId) : null;
    } else {
      const childList = answersRef.current?.[fieldKey];
      entry = Array.isArray(childList) ? childList.find((f) => f?.id === placeholderId) : null;
    }
    if (!entry?._file) return;
    setAnswers((prev) => {
      const next = setAnswerAtPath(prev, fieldKey, (cur) => cur.filter((f) => f?.id !== placeholderId));
      answersRef.current = next;
      return next;
    });
    await onUpload(fieldKey, [entry._file]);
  }, [onUpload]);

  // Optimistic remove + server delete. Derives `removed` from the ref BEFORE scheduling
  // the state update — capturing it inside the setAnswers updater is unsafe under React
  // concurrent rendering (updater may run multiple times, so the post-state server-delete
  // branch could read a stale/null ref).
  const onRemoveFile = useCallback(async (fieldKey, fileId) => {
    const m = NESTED_FIELD_KEY.exec(fieldKey);
    let currentList = null;
    if (m) {
      const [, section, rowIdxStr, child] = m;
      const rows = answersRef.current?.[section];
      const row = Array.isArray(rows) ? rows[Number(rowIdxStr)] : null;
      currentList = row ? row[child] : null;
    } else {
      currentList = answersRef.current?.[fieldKey];
    }
    const removed = Array.isArray(currentList)
      ? currentList.find((f) => f?.id === fileId || f?.fileId === fileId) || null
      : null;
    if (!removed) return;

    setAnswers((prev) => {
      const next = setAnswerAtPath(prev, fieldKey, (cur) =>
        cur.filter((f) => f?.id !== fileId && f?.fileId !== fileId),
      );
      answersRef.current = next;
      return next;
    });
    if (resourceId) {
      writeLocalDraft(resourceId, stripClientOnlyFields(answersRef.current));
    }

    // Only call server delete for real (server-known) descriptors. Pending/failed
    // placeholders never reached the server.
    const serverFileId = removed.fileId || (!removed.pending ? removed.id : null);
    if (!serverFileId || typeof adapter.deleteFile !== 'function') return;
    try {
      await adapter.deleteFile(fieldKey, serverFileId, grantToken);
    } catch (e) {
      // Rollback: re-insert the descriptor so the respondent still sees the file.
      setAnswers((prev) => {
        const next = setAnswerAtPath(prev, fieldKey, (cur) => [...cur, removed]);
        answersRef.current = next;
        return next;
      });
      throw e;
    }
  }, [adapter, grantToken, resourceId]);

  // Wrapper-facing: set state + ref in one call. Use from loadForm paths to avoid the
  // "forgot to update answersRef" footgun.
  const hydrate = useCallback((nextAnswers) => {
    const clean = nextAnswers && typeof nextAnswers === 'object' ? nextAnswers : {};
    setAnswers(clean);
    answersRef.current = clean;
  }, []);

  // Called by wrappers after their loadForm when the server returned no answers. Restores
  // the local backup if present and flashes "Draft restored" for 3 s.
  const restoreFromLocalDraft = useCallback(() => {
    if (!resourceId) return false;
    const local = readLocalDraft(resourceId);
    if (!local?.answers || Object.keys(local.answers).length === 0) return false;
    setAnswers(local.answers);
    answersRef.current = local.answers;
    if (local.savedAt) setLastSavedAt(local.savedAt);
    setAutoSaveStatus('restored');
    if (restoredClearTimerRef.current) clearTimeout(restoredClearTimerRef.current);
    restoredClearTimerRef.current = setTimeout(() => {
      setAutoSaveStatus((cur) => (cur === 'restored' ? null : cur));
      restoredClearTimerRef.current = null;
    }, RESTORED_STATUS_CLEAR_MS);
    return true;
  }, [resourceId]);

  // Called by wrappers after loadForm when the server includes a savedAt timestamp for
  // an existing draft. Accepts ms-epoch number OR ISO string.
  const setLastSavedAtFromServer = useCallback((ts) => {
    if (ts == null) return;
    const parsed = typeof ts === 'number' ? ts : Date.parse(ts);
    if (!Number.isNaN(parsed)) setLastSavedAt(parsed);
  }, []);

  // Called by wrappers on submit success (or anywhere the local backup is known stale).
  const clearLocalBackup = useCallback(() => {
    if (!resourceId) return;
    clearLocalDraft(resourceId);
  }, [resourceId]);

  // Unmount cleanup.
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (serverAutoSaveTimerRef.current) clearTimeout(serverAutoSaveTimerRef.current);
      if (restoredClearTimerRef.current) clearTimeout(restoredClearTimerRef.current);
    };
  }, []);

  return {
    answers,
    answersRef,
    autoSaveStatus,
    lastSavedAt,
    savingDraft,
    onChangeAnswer,
    onUpload,
    onRemoveFile,
    retryUpload,
    saveDraft,
    requestServerAutoSave,
    cancelPendingServerAutoSave,
    hydrate,
    restoreFromLocalDraft,
    setLastSavedAtFromServer,
    clearLocalBackup,
  };
}
